
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle, User, GlassWater, Shield, Music, Search, FileText, Theater, Megaphone, Disc } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";

export const VenueStaffPage = () => {
  const mockEmployees = [
    { id: 1, name: "Alex Johnson", role: "Bartender", email: "alex@example.com", phone: "(555) 123-4567", certified: true },
    { id: 2, name: "Sam Wilson", role: "Security", email: "sam@example.com", phone: "(555) 234-5678", certified: true },
    { id: 3, name: "DJ Max", role: "DJ", email: "max@example.com", phone: "(555) 345-6789", certified: false },
    { id: 4, name: "Jamie Smith", role: "Bartender", email: "jamie@example.com", phone: "(555) 456-7890", certified: true },
    { id: 5, name: "Casey Taylor", role: "Security", email: "casey@example.com", phone: "(555) 567-8901", certified: true },
    { id: 6, name: "Sophia Lee", role: "Performer", email: "sophia@example.com", phone: "(555) 678-9012", certified: false },
    { id: 7, name: "Mike Rodriguez", role: "Promoter", email: "mike@example.com", phone: "(555) 789-0123", certified: false },
    { id: 8, name: "Taylor Blake", role: "Promoter", email: "taylor@example.com", phone: "(555) 890-1234", certified: true },
  ];

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Bartender':
        return <GlassWater className="h-5 w-5 text-teal-500" />;
      case 'Security':
        return <Shield className="h-5 w-5 text-blue-500" />;
      case 'DJ':
        return <Disc className="h-5 w-5 text-purple-500" />;
      case 'Performer':
        return <Theater className="h-5 w-5 text-pink-500" />;
      case 'Promoter':
        return <Megaphone className="h-5 w-5 text-amber-500" />;
      default:
        return <User className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Employee Management</h1>
        <Button>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Employee
        </Button>
      </div>

      <div className="mb-6 flex">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search employees..."
            className="pl-8"
          />
        </div>
      </div>

      <Tabs defaultValue="all">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="all">All Employees</TabsTrigger>
          <TabsTrigger value="bartenders">Bartenders</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="djs">DJs</TabsTrigger>
          <TabsTrigger value="performers">Performers</TabsTrigger>
          <TabsTrigger value="promoters">Promoters</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Employee Directory</CardTitle>
              <CardDescription>Manage your venue employees</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees.map((employee) => (
                  <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                        {getRoleIcon(employee.role)}
                      </div>
                      <div>
                        <h3 className="font-semibold">{employee.name}</h3>
                        <p className="text-sm text-muted-foreground">{employee.role}</p>
                        <div className="flex items-center gap-2 text-sm">
                          <span>{employee.email}</span>
                          <span>•</span>
                          <span>{employee.phone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {employee.certified && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                          Certified
                        </span>
                      )}
                      <Button variant="outline" size="sm">View</Button>
                      <Button variant="ghost" size="sm">Edit</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bartenders" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GlassWater className="h-5 w-5 text-teal-500" />
                Bartenders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'Bartender')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <GlassWater className="h-5 w-5 text-teal-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {employee.certified && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                            RSA Certified
                          </span>
                        )}
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-500" />
                Security Employees
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'Security')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <Shield className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {employee.certified && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                            Certified
                          </span>
                        )}
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="djs" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Disc className="h-5 w-5 text-purple-500" />
                DJs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'DJ')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <Disc className="h-5 w-5 text-purple-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performers" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Theater className="h-5 w-5 text-pink-500" />
                Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'Performer')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <Theater className="h-5 w-5 text-pink-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promoters" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-amber-500" />
                Promoters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'Promoter')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <Megaphone className="h-5 w-5 text-amber-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {employee.certified && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                            Certified
                          </span>
                        )}
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Employee Documents
          </CardTitle>
          <CardDescription>
            Manage rosters, contracts, and employee certifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-500" />
                <span className="font-medium">Weekly Employee Roster</span>
              </div>
              <Button variant="outline" size="sm">View</Button>
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-500" />
                <span className="font-medium">Employee Training Records</span>
              </div>
              <Button variant="outline" size="sm">View</Button>
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-500" />
                <span className="font-medium">Certification Tracker</span>
              </div>
              <Button variant="outline" size="sm">View</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
